import type { Course, Vec2 } from '@sw/schema'
import { closestPointOnSegment, distance, lerp } from './vec.js'

export interface CourseIndex {
  points: readonly Vec2[]
  /** Distance along the course to each point. */
  cumulative: readonly number[]
  totalM: number
  corridorM: number
}

export function indexCourse(course: Course): CourseIndex {
  const cumulative: number[] = [0]
  for (let i = 1; i < course.points.length; i++) {
    cumulative.push(cumulative[i - 1] + distance(course.points[i - 1], course.points[i]))
  }
  return {
    points: course.points,
    cumulative,
    totalM: cumulative[cumulative.length - 1],
    corridorM: course.corridorM,
  }
}

export interface CourseFix {
  /** Distance travelled along the route. */
  chainageM: number
  /** How far off the route the listener is. */
  crossTrackM: number
  segmentIndex: number
  /** False once the listener strays outside the course corridor. */
  onCourse: boolean
}

export interface ProjectOptions {
  /**
   * The listener's previous chainage. Supplying it confines the search to a
   * window around where they already were, which is what keeps a course that
   * crosses or doubles back on itself from teleporting a runner kilometres
   * along the route because a stray fix landed near a later leg.
   */
  previousChainageM?: number
  /** How far along the course the listener could plausibly have moved. */
  searchWindowM?: number
}

const DEFAULT_SEARCH_WINDOW_M = 250

/** Map-matches a position onto the course. */
export function projectOntoCourse(
  index: CourseIndex,
  position: Vec2,
  options: ProjectOptions = {},
): CourseFix {
  const windowed = findNearest(index, position, options)
  const best = windowed ?? findNearest(index, position, {})

  // A course always has at least two points, so a nearest segment always exists.
  /* c8 ignore next */
  if (!best) throw new Error('Course has no segments')

  return { ...best, onCourse: best.crossTrackM <= index.corridorM }
}

function findNearest(
  index: CourseIndex,
  position: Vec2,
  options: ProjectOptions,
): Omit<CourseFix, 'onCourse'> | undefined {
  const window = options.searchWindowM ?? DEFAULT_SEARCH_WINDOW_M
  const from = options.previousChainageM === undefined ? undefined : options.previousChainageM - window
  const to = options.previousChainageM === undefined ? undefined : options.previousChainageM + window

  let best: Omit<CourseFix, 'onCourse'> | undefined

  for (let i = 0; i < index.points.length - 1; i++) {
    const segmentStart = index.cumulative[i]
    const segmentEnd = index.cumulative[i + 1]
    if (from !== undefined && to !== undefined && (segmentEnd < from || segmentStart > to)) {
      continue
    }

    const { point, t } = closestPointOnSegment(position, index.points[i], index.points[i + 1])
    const crossTrackM = distance(position, point)
    if (best === undefined || crossTrackM < best.crossTrackM) {
      best = {
        chainageM: segmentStart + (segmentEnd - segmentStart) * t,
        crossTrackM,
        segmentIndex: i,
      }
    }
  }

  return best
}

/** The point a given distance along the course, clamped to its ends. */
export function pointAtChainage(index: CourseIndex, chainageM: number): Vec2 {
  if (chainageM <= 0) return index.points[0]
  if (chainageM >= index.totalM) return index.points[index.points.length - 1]

  for (let i = 0; i < index.points.length - 1; i++) {
    const start = index.cumulative[i]
    const end = index.cumulative[i + 1]
    if (chainageM <= end) {
      const span = end - start
      const t = span === 0 ? 0 : (chainageM - start) / span
      return lerp(index.points[i], index.points[i + 1], t)
    }
  }
  /* c8 ignore next */
  return index.points[index.points.length - 1]
}
